import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import LanguageSwitch from '../../Common/LanguageSwitch';
import CommonInput from '../../Common/CommonInput';
import CommonDate from '../../Common/CommonDate';
import CommonAsyncSelect from '../../Common/CommonAsyncSelect';
import { fetchSettings } from '../../../store/modules/jobcosting/jobcostingActions';
import useFormSettings from '../../../hooks/useFormSettings';

import jobOrderService from '../../../services/joborderService';

import useModal from '../../../hooks/useModal';
import DataSelectionModal from '../../Common/CommonModal/DataSelectionModal';
import AllJobOrders from '../../../pages/Alljoborders/Alljoborders';
import { useHasPermission } from '../../../utils/permissionUtils';

const JobCostingForm = ({ initialData, onSave, isLoading, onClose }) => {

    const hasCreatePermission = useHasPermission(['create job costings']);
    const hasUpdatePermission = useHasPermission(['update job costings']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('jobcosting', fetchSettings);
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [formData, setFormData] = useState({
        id: '',
        costing_number: '',
        costing_date: '',
        job_card_date: '',
        customer: null,
        customer_name: '',
        address1: '',
        address2: '',
        phone_number: '',
        location: null,
        is_lpo: false,
        lpo_number: '',
        lpo_date: '',
        total_job_cost: '',
        total_material_cost: '',
        total_labour_cost: '',
        total_production: '',

        gross_total: '',
        discount_percentage: '',
        discount_amount: '',
        sub_total: '',
        vat_amount: '',
        net_total: '',

        finished_goods: [],
        labours: [],
        raw_materials: [],
        items: [],
    });

    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingCostingItems, setIsLoadingCostingItems] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                id: initialData.id || '',
                costing_number: initialData.costing_number || '',
                costing_date: initialData.costing_date || '',
                job_card_date: initialData.job_card_date || '',
                customer: initialData.customer || null,
                customer_name: initialData.customer_name || '',
                address1: initialData.address1 || '',
                address2: initialData.address2 || '',
                phone_number: initialData.phone_number || '',
                location: initialData.location || null,
                is_lpo: initialData.is_lpo || false,
                lpo_number: initialData.lpo_number || '',
                lpo_date: initialData.lpo_date || '',
                total_job_cost: initialData.total_job_cost || '',
                total_material_cost: initialData.total_material_cost || '',
                total_labour_cost: initialData.total_labour_cost || '',
                total_production: initialData.total_production || '',
                
                gross_total: initialData.gross_total || '',
                discount_percentage: initialData.discount_percentage || '',
                discount_amount: initialData.discount_amount || '',
                sub_total: initialData.sub_total || '',
                vat_amount: initialData.vat_amount || '',
                net_total: initialData.net_total || '',

                finished_goods: initialData.finished_goods || [],
                labours: initialData.labours || [],
                raw_materials: initialData.raw_materials || [],
                items: initialData.items || [],
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSelectChange = async (name, value) => {
        
        if(name === 'job_order') {
            setFormData(prev => ({
                ...prev,
                [name]: value ?? null,
                job_card_date: value?.order_date ?? '',
                customer_name: value?.customer_name ?? '',
                address1: value?.address1 ?? '',
                address2: value?.address2 ?? '',
                phone_number: value?.phone_number ?? '',
                is_lpo: value?.is_lpo ? true : false,
                lpo_number: value?.is_lpo ? (value?.lpo_number ?? '') : '',
                lpo_date: value?.is_lpo ? (value?.lpo_date ?? '') : '',
                location: value?.location ?? null,
            }));
            
            if (value) {
                try {
                    setIsLoadingCostingItems(true);
                    const response = await jobOrderService.getJobCostings(value.id);

                    setFormData(prev => ({
                        ...prev,
                        raw_materials: response?.raw_materials,
                        finished_goods: response?.finished_goods,
                        labours: response?.labours,
                        items: response?.items,
                        total_job_cost: response?.net_total,
                        total_material_cost: response?.total_material_cost,
                        total_labour_cost: response?.total_labour_cost,
                        total_production: response?.total_production,

                        gross_total: response?.gross_total,
                        discount_percentage: response?.discount_percentage,
                        discount_amount: response?.discount_amount,
                        sub_total: response?.sub_total,
                        vat_amount: response?.vat_amount,
                        net_total: response?.net_total,
                    }));
                } catch (error) {
                    console.error('Error fetching FG items:', error);
                } finally {
                    setIsLoadingCostingItems(false);
                }
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const refreshJobCosting = async (jobOrderId) => {
        try {
            setIsLoadingCostingItems(true);
            const response = await jobOrderService.getJobCostings(jobOrderId);

            setFormData(prev => ({
                ...prev,
                raw_materials: response?.raw_materials,
                finished_goods: response?.finished_goods,
                labours: response?.labours,
                items: response?.items,
                total_job_cost: response?.net_total,
                total_material_cost: response?.total_material_cost,
                total_labour_cost: response?.total_labour_cost,
                total_production: response?.total_production,

                gross_total: response?.gross_total,
                discount_percentage: response?.discount_percentage,
                discount_amount: response?.discount_amount,
                sub_total: response?.sub_total,
                vat_amount: response?.vat_amount,
                net_total: response?.net_total,
            }));
        } catch (error) {
            console.error('Error fetching FG items:', error);
        } finally {
            setIsLoadingCostingItems(false);
        }
    }



    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate finished goods production quantities
        const productionErrors = {};
        if (formData.finished_goods && formData.finished_goods.length > 0) {
            const incompleteItems = formData.finished_goods.filter(fg => fg.qty_produced < fg.order_quantity);
            if (incompleteItems.length > 0) {
                productionErrors.finished_goods = `Some finished goods have incomplete production. Please ensure all items meet their order quantities.`;
            }
        }
        
        if (Object.keys(productionErrors).length > 0) {
            setErrors(productionErrors);
            return;
        }
        
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || isLoadingCostingItems || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    const jobOrderModal = useModal();
    const jobOrderExternalCondition = {
        rules: {
            condition: 'and',
            children: [
                {
                    type: 'rule',
                    query: {
                        operator: 'in',
                        rule: 'job_orders.status',
                        type: 'text',
                        value: ['2', '3']
                    }
                }
            ]
        }
    };

    const handleJobOrderSelected = (jobOrder) => {
        const updatedValue = {
            id: jobOrder.id,
            label: jobOrder.order_number +' ( '+jobOrder.customer_name+' )',
            customer_name: jobOrder.customer_name,
            order_date: jobOrder.order_date,
            address1: jobOrder.address1,
            address2: jobOrder.address2,
            phone_number: jobOrder.phone_number,
            is_lpo: jobOrder.is_lpo,
            lpo_number: jobOrder.lpo_number,
            lpo_date: jobOrder.lpo_date,
            location: jobOrder.location,
        }
        handleSelectChange('job_order', updatedValue);
        jobOrderModal.hide();
    };

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Create Job Costing</h5>
                </div>
                <div className="d-flex justify-content-end">
                </div>
            </div>
            <div className="flex-grow-1">

                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="costing_number"
                            label="Costing Number"
                            value={formData.costing_number}
                            onChange={handleChange}
                            placeholder="Enter costing number"
                            disabled={true}
                            errors={errors}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="costing_date"
                            label="Costing Date"
                            value={formData.costing_date}
                            onChange={handleChange}
                            placeholder="Enter costing date"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="job_card_date"
                            label="Job Card Date"
                            value={formData.job_card_date}
                            onChange={handleChange}
                            placeholder="Enter job card date"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4">
                        <CommonAsyncSelect
                            name="job_order"
                            label="Job Order"
                            value={formData.job_order}
                            onChange={(value) => handleSelectChange('job_order', value)}
                            loadOptions={async (inputValue) => {
                                const condition = {
                                    rules: {
                                        condition: 'and',
                                        children: [
                                            {
                                                type: 'rule',
                                                query: {
                                                    operator: 'in',
                                                    rule: 'job_orders.status',
                                                    type: 'text',
                                                    value: ['2', '3']
                                                }
                                            }
                                        ]
                                    }
                                }

                                const response = await jobOrderService.getAllList({ search: inputValue }, condition);
                                return response.data.map(job => ({
                                    id: job.id,
                                    label: job.order_number +' ( '+job.customer_name+' )',
                                    customer_name: job.customer_name,
                                    order_date: job.order_date,
                                    address1: job.address1,
                                    address2: job.address2,
                                    phone_number: job.phone_number,
                                    is_lpo: job.is_lpo,
                                    lpo_number: job.lpo_number,
                                    lpo_date: job.lpo_date,
                                }));
                            }}
                            isSubmitting={isSubmitting || formData?.id}
                            errors={errors}
                            required={true}
                            actionButtons={[
                                {
                                    icon: 'fa fa-bars',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => jobOrderModal.show()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="customer_name"
                            label="Customer Name"
                            value={formData.customer_name}
                            required={false}
                            disabled={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="phone_number"
                            label="Phone Number"
                            value={formData.phone_number}
                            required={false}
                            disabled={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="address1"
                            label="Address 1"
                            value={formData.address1}
                            required={false}
                            disabled={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="address2"
                            label="Address 2"
                            value={formData.address2}
                            required={false}
                            disabled={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="location"
                            label="Store Location"
                            value={formData.location?.label}
                            required={false}
                            disabled={true}
                        />
                    </div>
                </div>
                {formData.is_lpo && (
                    <div className="row">
                        <div className="col-md-4">
                            <CommonInput
                                name="lpo_number"
                                label="LPO Number"
                                onChange={handleChange}
                                value={formData.lpo_number || ''}
                                required={false}
                                disabled={true}
                            />
                        </div>
                        <div className="col-md-4">
                            <CommonInput
                                name="lpo_date"
                                label="LPO Date"
                                onChange={handleChange}
                                value={formData.lpo_date || ''}
                                required={false}
                                disabled={true}
                            />
                        </div>
                    </div>
                )}

                <div className="row">
                    <div className="col-md-8">

                        <h5 className="mb-3">Raw Materials</h5>
                        <div className="table-responsive mb-2">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th className="text-nowrap">FGCode</th>
                                        <th className="text-nowrap">UsedForFGName</th>
                                        <th className="text-nowrap">MaterialCode</th>
                                        <th className="text-nowrap">Material</th>
                                        <th className="text-nowrap">UOM</th>
                                        <th className="text-nowrap">Quantity</th>
                                        <th className="text-nowrap">Cost</th>
                                        <th className="text-nowrap">TotalCost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.raw_materials.length > 0 ? (
                                        formData.raw_materials.map((material, index) => (
                                            <tr key={index}>
                                                <td className="text-nowrap">{material.fg_code}</td>
                                                <td className="text-nowrap">{material.fg_name}</td>
                                                <td>{material.item_code}</td>
                                                <td className="text-nowrap">{material.item_name}</td>
                                                <td>{material.unit}</td>
                                                <td>{material.item_quantity}</td>
                                                <td>{material.unit_price}</td>
                                                <td>{material.line_amount}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center">No records</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <h5 className="mb-3">Labour</h5>
                        <div className="table-responsive mb-2">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th className="text-nowrap">FGCode</th>
                                        <th className="text-nowrap">FGName</th>
                                        <th className="text-nowrap">TailorID</th>
                                        <th className="text-nowrap">TailorName</th>
                                        <th className="text-nowrap">QuantityProduced</th>
                                        <th className="text-nowrap">ChargePerUnit</th>
                                        <th className="text-nowrap">LaborCharges</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.labours.length > 0 ? (
                                        formData.labours.map((labour, index) => (
                                            <tr key={index}>
                                                <td className="text-nowrap">{labour.fg_code}</td>
                                                <td className="text-nowrap">{labour.fg_name}</td>
                                                <td>{labour.tailor_code}</td>
                                                <td>{labour.tailor_name}</td>
                                                <td>{labour.qty_produced}</td>
                                                <td>{labour.unit_amount}</td>
                                                <td>{labour.amount_to_pay}</td>
                                            </tr>
                                        ))  
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center">No records</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>


                        <h5 className="mb-3">Finished Goods</h5> 
                        {errors.finished_goods && (
                            <div className="alert alert-danger mb-3">
                                {errors.finished_goods}
                            </div>
                        )}
                        <div className="table-responsive mb-2">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th className="text-nowrap">FGCode</th>
                                        <th className="text-nowrap">FGName</th>
                                        <th className="text-nowrap">Qty Ordered</th>
                                        <th className="text-nowrap">Qty Produced</th>
                                        <th className="text-nowrap">Total Cost</th>
                                        <th className="text-nowrap">RM Cost</th>
                                        <th className="text-nowrap">Labour Cost</th>
                                        <th className="text-nowrap">ProductionCost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.finished_goods.length > 0 ? (
                                        formData.finished_goods.map((fg, index) => (
                                            <tr key={index}>
                                                <td className="text-nowrap">{fg.fg_code}</td>
                                                <td className="text-nowrap">{fg.fg_name}</td>
                                                <td>{fg.order_quantity}</td>
                                                <td style={{
                                                    backgroundColor: fg.qty_produced < fg.order_quantity ? '#ffebee' : '#e8f5e8'
                                                }}>{fg.qty_produced}</td>
                                                <td>{fg.order_amount}</td>
                                                <td>{fg.material_cost}</td>
                                                <td>{fg.labour_cost}</td>
                                                <td>{fg.production_cost}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center">No records</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="total_job_cost"
                            label="Total Job Order Value"
                            value={formData.total_job_cost}
                            required={false}
                            disabled={true}
                        />
                        <CommonInput
                            name="total_material_cost"
                            label="Total Job Raw Material Cost"
                            value={formData.total_material_cost}
                            required={false}
                            disabled={true}
                        />
                        <CommonInput
                            name="total_labour_cost"
                            label="Total Job Labour Cost"
                            value={formData.total_labour_cost}
                            required={false}
                            disabled={true}
                        />
                        <CommonInput
                            name="total_production"
                            label="Total Job Production Cost"
                            value={formData.total_production}
                            required={false}
                            disabled={true}
                        />
                        {formData?.id && formData?.id != '' && formData?.job_order?.id && (
                            <div className="mb-3 p-4 border-primary border rounded-3">
                                <div>
                                    This job already has a costing record. Refresh to fetch latest data and save/update existing record.
                            </div>
                            <button type="button" className="btn btn-primary mt-4" onClick={() => refreshJobCosting(formData?.job_order?.id)}>
                                    Refresh
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <DataSelectionModal
                    show={jobOrderModal.isVisible}
                    onHide={jobOrderModal.hide}
                    title=""
                    DataComponent={AllJobOrders}
                    selectionMode="single"
                    onDataSelected={handleJobOrderSelected}
                    componentProps={{
                        // Any props specific to Suppliers component
                        excludedActions: ['create', 'actions', 'filter'],
                        externalCondition: jobOrderExternalCondition
                    }}
                />
            </div>

            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading...
                        </>
                    ) : isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default JobCostingForm; 